/**
 * Trunnion AI - Focus Trap Component
 *
 * Constrains keyboard focus to a container element while active.
 * Used by modals, slide-over panels, and any overlay that requires
 * the user to interact before returning to the page behind it.
 *
 * Tab / Shift+Tab cycles through focusable descendants; focus cannot
 * escape the container until the trap is deactivated.
 */

import { useEffect, useRef, type ReactNode } from 'react';

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(', ');

interface FocusTrapProps {
  children: ReactNode;
  /** When true the trap is engaged and focus is constrained. */
  active: boolean;
  /** Return focus to the element that was focused before the trap activated. Defaults to true. */
  returnFocus?: boolean;
}

export function FocusTrap({ children, active, returnFocus = true }: FocusTrapProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const previouslyFocusedRef = useRef<HTMLElement | null>(null);

  // ── Capture the element that had focus before the trap activates ───
  useEffect(() => {
    if (active) {
      previouslyFocusedRef.current = document.activeElement as HTMLElement | null;
    }
  }, [active]);

  // ── Focus the first focusable element when the trap activates ─────
  useEffect(() => {
    if (!active || !containerRef.current) return;

    const focusable = containerRef.current.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR);
    if (focusable.length > 0) {
      focusable[0].focus();
    }
  }, [active]);

  // ── Keyboard trap: cycle Tab / Shift+Tab within the container ─────
  useEffect(() => {
    if (!active || !containerRef.current) return;

    const container = containerRef.current;

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key !== 'Tab') return;

      const focusable = container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR);
      if (focusable.length === 0) {
        // Nothing to focus -- prevent Tab from leaving the trap
        event.preventDefault();
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey) {
        // Shift+Tab: wrap from first to last
        if (document.activeElement === first) {
          event.preventDefault();
          last.focus();
        }
      } else {
        // Tab: wrap from last to first
        if (document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }
    }

    container.addEventListener('keydown', handleKeyDown);

    return () => {
      container.removeEventListener('keydown', handleKeyDown);
    };
  }, [active]);

  // ── Return focus when the trap deactivates ────────────────────────
  useEffect(() => {
    // This cleanup function runs when `active` transitions from true to false
    // or when the component unmounts.
    return () => {
      if (returnFocus && previouslyFocusedRef.current) {
        // Use a microtask so the DOM has finished updating before we
        // attempt to restore focus.
        const elementToRestore = previouslyFocusedRef.current;
        requestAnimationFrame(() => {
          if (elementToRestore && typeof elementToRestore.focus === 'function') {
            elementToRestore.focus();
          }
        });
        previouslyFocusedRef.current = null;
      }
    };
  }, [active, returnFocus]);

  return (
    <div ref={containerRef} style={{ display: 'contents' }}>
      {children}
    </div>
  );
}
